import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';

interface UiState {
  theme: Theme;
  sidebarCollapsed: boolean;
  commandMenuOpen: boolean;

  setTheme: (theme: Theme) => void;
  toggleSidebar: () => void;
  setCommandMenuOpen: (open: boolean) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      sidebarCollapsed: false,
      commandMenuOpen: false,

      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
      },

      toggleSidebar: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),
      setCommandMenuOpen: (commandMenuOpen) => set({ commandMenuOpen }),
    }),
    {
      name: 'postpilot-ui',
      // `commandMenuOpen` is deliberately NOT persisted — reloading the page into
      // an open command palette would be baffling.
      partialize: (state) => ({ theme: state.theme, sidebarCollapsed: state.sidebarCollapsed }),
    },
  ),
);

/**
 * Writes the theme to the DOM.
 *
 * The `dark` class is what every Tailwind `dark:` variant keys off, so this one
 * line is the entire theming mechanism. It has to be applied imperatively (not
 * rendered) because it lives on <html>, outside React's tree.
 *
 * The initial application happens in a blocking script in index.html — see the
 * comment there for why doing it in React would flash white on every load.
 */
export function applyTheme(theme: Theme): void {
  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  document.documentElement.classList.toggle('dark', isDark);

  // Persisted under the key the inline script reads. 'system' is stored as the
  // *resolved* value so the script does not have to re-evaluate the media query
  // before paint.
  localStorage.setItem('postpilot-theme', isDark ? 'dark' : 'light');
}

/**
 * Follows the OS if the user picked "system". Returns an unsubscribe.
 * Without this, a user on `system` who flips their OS to dark at 6pm keeps
 * staring at a light UI until they reload.
 */
export function watchSystemTheme(): () => void {
  const media = window.matchMedia('(prefers-color-scheme: dark)');

  const handler = () => {
    if (useUiStore.getState().theme === 'system') {
      applyTheme('system');
    }
  };

  media.addEventListener('change', handler);
  return () => media.removeEventListener('change', handler);
}
